import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { absUrl } from "@/lib/config";
import { Trophy, Medal, Sparkle } from "@phosphor-icons/react";

const tierRing = {
  bronze: "ring-[#CD7F32]",
  silver: "ring-[#C0C0C0]",
  gold: "ring-[#FFD700]",
};

export default function Gamification() {
  const { user } = useAuth();
  const [scope, setScope] = useState("month");
  const [projectId, setProjectId] = useState("");
  const [projects, setProjects] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [ach, setAch] = useState({ earned: [], locked: [], catalog: {} });
  const [champion, setChampion] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/projects").then(r => setProjects(r.data || [])); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [lb, a, ch] = await Promise.all([
        api.get("/gamification/leaderboard", { params: { scope, ...(projectId ? { project_id: projectId } : {}) } }),
        api.get("/gamification/achievements/me"),
        api.get("/gamification/champion", { params: { scope } }),
      ]);
      setLeaderboard(lb.data.rows || []);
      setAch(a.data || { earned: [], locked: [], catalog: {} });
      setChampion(ch.data.champion || null);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope, projectId]);

  const myEarnedCount = ach.earned.length;
  const totalPointsFromAch = ach.earned.reduce((s, a) => s + (a.points || 0), 0);

  return (
    <div>
      <div className="border-b border-border px-8 lg:px-12 py-8">
        <div className="eyebrow mb-2">Play / Recognition</div>
        <h1 className="font-display text-4xl font-black tracking-tight flex items-center gap-3">
          <Trophy size={36} weight="fill" className="text-[#FFD700]" />
          Champions & Achievements
        </h1>
        <p className="text-sm text-muted-foreground mt-2">Earn badges by shipping tasks. Climb the leaderboard by hitting milestones.</p>

        {/* Controls */}
        <div className="mt-6 flex flex-wrap gap-3">
          <div className="border border-border w-fit flex">
            {["week", "month", "all"].map(s => (
              <button key={s} onClick={() => setScope(s)}
                className={`px-4 py-2 text-xs font-mono-mini uppercase ${scope === s ? "bg-[#0A0A0A] text-white" : "hover:bg-muted"}`}
                data-testid={`scope-${s}`}>{s === "all" ? "All time" : `Last ${s}`}</button>
            ))}
          </div>
          <select value={projectId} onChange={e => setProjectId(e.target.value)}
            className="border border-border bg-white px-3 py-2 text-sm" data-testid="project-filter">
            <option value="">All projects (org-wide)</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
      </div>

      {/* Champion callout */}
      {champion && (
        <div className="border-b border-border px-8 lg:px-12 py-8 bg-gradient-to-r from-[#FFF8DC] to-white">
          <div className="flex items-center gap-6 flex-wrap" data-testid="champion-card">
            <div className="relative">
              {champion.avatar ? (
                <img src={absUrl(champion.avatar)} alt={champion.user_name} className="w-20 h-20 object-cover ring-4 ring-[#FFD700]" />
              ) : (
                <div className="w-20 h-20 bg-[#0A0A0A] text-white font-display text-3xl font-black flex items-center justify-center ring-4 ring-[#FFD700]">
                  {champion.user_name?.[0]}
                </div>
              )}
              <div className="absolute -top-2 -right-2 bg-[#FFD700] text-black w-9 h-9 rounded-full flex items-center justify-center text-2xl shadow-lg">
                👑
              </div>
            </div>
            <div>
              <div className="eyebrow">Current {scope === "all" ? "all-time" : scope} champion</div>
              <div className="font-display text-3xl font-black mt-1">{champion.user_name}</div>
              <div className="font-mono-mini text-sm text-muted-foreground mt-1">
                {champion.points} points · {champion.tasks_done} tasks done
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 border-b border-border">
        {/* Leaderboard */}
        <div className="border-r border-border p-8 lg:p-12">
          <div className="flex items-center gap-3 mb-6">
            <Medal size={22} weight="bold" />
            <div className="eyebrow">Leaderboard</div>
          </div>
          {leaderboard.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center border border-dashed border-border">
              No completed tasks in this range yet.
            </div>
          ) : (
            <div className="space-y-2">
              {leaderboard.slice(0, 20).map((row, i) => (
                <div key={row.user_id} className={`flex items-center gap-3 border border-border p-3 ${row.user_id === user.id ? "bg-blue-50" : ""}`} data-testid={`lb-row-${row.rank}`}>
                  <div className={`w-8 h-8 flex items-center justify-center font-display font-black text-sm
                    ${i === 0 ? "bg-[#FFD700] text-black" : i === 1 ? "bg-[#C0C0C0] text-black" : i === 2 ? "bg-[#CD7F32] text-white" : "bg-muted"}`}>
                    {row.rank}
                  </div>
                  {row.avatar ? (
                    <img src={absUrl(row.avatar)} alt={row.user_name} className="w-9 h-9 object-cover" />
                  ) : (
                    <div className="w-9 h-9 bg-[#0A0A0A] text-white flex items-center justify-center text-sm font-bold">{row.user_name?.[0]}</div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{row.user_name}</div>
                    <div className="text-[10px] font-mono-mini text-muted-foreground uppercase">{row.role}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-lg font-black">{row.points}</div>
                    <div className="text-[10px] font-mono-mini text-muted-foreground">{row.tasks_done} tasks</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* My Achievements */}
        <div className="p-8 lg:p-12">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Sparkle size={22} weight="bold" />
              <div className="eyebrow">Your Trophy Cabinet</div>
            </div>
            <div className="font-mono-mini text-xs text-muted-foreground">
              {myEarnedCount} / {myEarnedCount + ach.locked.length} · +{totalPointsFromAch} pts
            </div>
          </div>

          {ach.earned.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center border border-dashed border-border mb-6">
              Complete tasks to earn your first achievement!
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-8">
              {ach.earned.map(a => (
                <div key={a.id} className={`border-2 border-border p-3 flex flex-col items-center text-center ring-2 ring-offset-2 ${tierRing[a.tier] || "ring-[#0A0A0A]"}`}
                  style={{ borderColor: a.color }} data-testid={`ach-${a.key}`}>
                  <div className="text-4xl mb-1">{a.icon}</div>
                  <div className="font-semibold text-xs">{a.label}</div>
                  <div className="text-[10px] font-mono-mini text-muted-foreground mt-1">+{a.points} pts</div>
                </div>
              ))}
            </div>
          )}

          {ach.locked.length > 0 && (
            <>
              <div className="eyebrow mb-3">Locked</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {ach.locked.map(a => (
                  <div key={a.key} className="border border-dashed border-border p-3 flex flex-col items-center text-center opacity-40 grayscale">
                    <div className="text-3xl mb-1">{a.icon}</div>
                    <div className="font-semibold text-xs">{a.label}</div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">{a.description}</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
